import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import DOMPurify from 'dompurify';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';
import GeneralUtils from '../../utilities/GeneralUtils';
import DataTableUtils from '../../utilities/DataTableUtils';
import CompetitionUtils from '../../utilities/CompetitionUtils';

const CompetitionJobDetailsOldComponent = (props) => {
    const [competition, setCompetition] = useState(null);
    const [jobs, setJobs] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        setCompetition(props.competition);

        const getJobs = async () => {
            await competitionService.getCompetitionJobs(props?.competition.id)
                .then((_jobs) => {
                    setJobs(_jobs);
                })
                .catch((error) => toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getJobs();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionService, props]);

    const getJobDetails = (data) => {
        return (
            <>
                {data.jobClass && <div><span className="font-bold">{translatedMessage("job.jobClass") + ": "}</span>{data.jobClass}</div>}
                {data.level && <div><span className="font-bold">{translatedMessage("job.level") + ": "}</span>{data.level}</div>}
                {data.departmentType && <div><span className="font-bold">{translatedMessage("job.departmentType") + ": "}</span>{data.departmentType}</div>}
                {data.departmentName && <div><span className="font-bold">{translatedMessage("job.departmentName") + ": "}</span>{data.departmentName}</div>}
            </>
        );
    };

    const getJobLocation = (data) => {
        return (
            <>
                {data.institution?.name && <div>{data.institution.name}</div>}
                {data.institution?.locality && <div><span className="font-bold">{translatedMessage("generic.locality") + ": "}</span>{data.institution.locality}</div>}
                {data.institution?.county && <div><span className="font-bold">{translatedMessage("generic.county") + ": "}</span>{data.institution.county}</div>}
            </>
        );
    };

    DOMPurify.addHook('afterSanitizeAttributes', function (node) {
        // set all elements owning target to target=_blank
        if ('target' in node) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener');
        }
    });

    return (
        <>
            {/* For backwad compatibility, when details about the tests were kept in the competition entity*/}
            <div className='col-12 md:col-6'>
                <div className='filed-label'>{translatedMessage('competition.institution')}</div>
                <div className='filed-value'>{competition?.institution?.name ? competition?.institution.name : "-"}</div>
            </div>
            <div className="col-12 md:col-6">
                <div className="filed-label">{translatedMessage('generic.status')}</div>
                <div className="filed-value">
                    <span className={`w-min status status-competition-${competition?.status.toString().toLowerCase()}`}>
                        {CompetitionUtils.getCompetitionStatus(competition)}
                    </span>
                </div>
            </div>
            <div className="col-12">
                <div className="filed-label">{translatedMessage('competition.positionCategory')}</div>
                <div className="filed-value">
                    {competition?.positionCategory ? translatedMessage("PositionCategory." + competition?.positionCategory) : "-"}
                </div>
            </div>
            <div className='col-12 md:col-6'>
                <div className='filed-label'>{translatedMessage('competition.startDate')}</div>
                <div className='filed-value'>{GeneralUtils.formatDate(competition?.startDate)}</div>
            </div>
            <div className='col-12 md:col-6'>
                <div className='filed-label'>{translatedMessage('competition.submitEndDate')}</div>
                <div className='filed-value'>{GeneralUtils.formatDate(competition?.submitEndDate)}</div>
            </div>
            <div className='col-12 md:col-6'>
                <div className='filed-label'>{translatedMessage('competition.clarificationInterval')}</div>
                <div className='filed-value'>{competition?.clarificationInterval} {translatedMessage("generic.days")}</div>
            </div>
            <div className='col-12 md:col-6'>
                <div className='filed-label'>{translatedMessage('competition.contestationInterval')}</div>
                <div className='filed-value'>{competition?.contestationInterval} {translatedMessage("generic.days")}</div>
            </div>
            <div className='col-12 md:col-6'>
                <div className='filed-label'>{translatedMessage('competition.writtenTestDate')}</div>
                <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.writtenTestDate)}</div>
            </div>
            <div className='col-12 md:col-6'>
                <div className='filed-label'>{translatedMessage('competition.interviewTestDate')}</div>
                <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.interviewTestDate)}</div>
            </div>

            {competition?.supplementaryTest1Name &&
                <>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest1Name')}</div>
                        <div className='filed-value'>{competition?.supplementaryTest1Name}</div>
                    </div>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest1Date')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.supplementaryTest1Date)}</div>
                    </div>
                </>
            }

            {competition?.supplementaryTest2Name &&
                <>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest2Name')}</div>
                        <div className='filed-value'>{competition?.supplementaryTest2Name}</div>
                    </div>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest2Date')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.supplementaryTest2Date)}</div>
                    </div>
                </>
            }
            {competition?.supplementaryTest3Name &&
                <>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest3Name')}</div>
                        <div className='filed-value'>{competition?.supplementaryTest3Name}</div>
                    </div>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest3Date')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.supplementaryTest3Date)}</div>
                    </div>
                </>
            }

            {props?.hideJobs !== true && jobs.length > 0 &&
                <div className='col-12 mb-3'>
                    <div className='filed-label mt-3 mb-2'>{translatedMessage('job.paragraphTitle')}</div>
                    <DataTable
                        ref={dt}
                        value={jobs}
                        dataKey="id"
                        paginator
                        rows={DataTableUtils.defalRowsPerPage()}
                        rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                        className="datatable-responsive pcn-datatable"
                        responsiveLayout="scroll"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                        emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                    >
                        <Column
                            field="position"
                            header={translatedMessage('job.position')}
                            sortable
                            headerStyle={{ width: '25%', minWidth: '10rem' }}
                            filter
                            showFilterMenu={false}
                        />
                        <Column
                            field="jobClass"
                            header={translatedMessage('generic.details')}
                            sortable
                            headerStyle={{ width: '25%', minWidth: '10rem' }}
                            body={getJobDetails}
                            filter
                            showFilterMenu={false}
                        />
                        <Column
                            field="institution.name"
                            header={translatedMessage('job.location')}
                            sortable
                            headerStyle={{ width: '25%', minWidth: '10rem' }}
                            body={getJobLocation}
                            filter
                            showFilterMenu={false}
                        />
                    </DataTable>
                </div>
            }

            <div className='col-12'>
                <div className='filed-label'>{translatedMessage('generic.description')}</div>
                <div className='filed-value'>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(competition?.description) }} />
                </div>
            </div>
            <div className='col-12'>
                <div className='filed-label'>{translatedMessage('competition.bibliography')}</div>
                <div className='filed-value'>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(competition?.bibliography) }} />
                </div>
            </div>
        </>
    );
};

export default CompetitionJobDetailsOldComponent;
